// Talentspeicher in der Talentliste. Reine Helfer für Tabelle, CSV und
// Massenaktionen, ohne React und ohne helpers/index.js, damit Jest sie direkt
// testen kann.
//
// Gesperrte Zeilen kommen schon maskiert aus useTalentsQuery (storage_locked,
// storage_rank, score '-', ohne Matching und ohne Zusatzangaben). Solange die
// Entitlements eines Plan-Kunden nicht feststehen, sind alle gezählten Zeilen
// vorläufig maskiert und tragen zusätzlich storage_pending. Ohne Sperren
// (legacy, preview, unter dem Limit) fehlen beide Felder.

import { SPACE_ACTION_IDS } from '../constants/space-action-ids'

// Spalte und Filter-ID sind gleich, so erwartet es use-filters-from-url.js.
export const TALENT_STORAGE_COLUMN = 'talent_storage'

export const TALENT_STORAGE_VALUES = Object.freeze({
  VISIBLE: 'VISIBLE',
  LOCKED: 'LOCKED'
})

// Für einen Link auf die gesperrten Talente, als computed-Parameter wie
// OLD_TALENTS_FILTER im Limit-Dialog (localStorage setzen, dann navigieren)
export const LOCKED_TALENTS_FILTER = Object.freeze([
  Object.freeze({
    [TALENT_STORAGE_COLUMN]: Object.freeze({
      in: Object.freeze([TALENT_STORAGE_VALUES.LOCKED])
    })
  })
])

export const isStorageLocked = (space) => space?.storage_locked === true

export const isStoragePending = (space) => space?.storage_pending === true

export const talentStorageValueOf = (space) =>
  isStorageLocked(space)
    ? TALENT_STORAGE_VALUES.LOCKED
    : TALENT_STORAGE_VALUES.VISIBLE

// Endgültig gesperrte Talente. Vorläufig maskierte zählen nicht, die Liste
// wartet in dem Fall ohnehin auf die Entitlements.
export const countLockedTalents = (spaces) =>
  (Array.isArray(spaces) ? spaces : []).filter(
    (space) => isStorageLocked(space) && !isStoragePending(space)
  ).length

// Massenaktionen, die echte Ergebnisse laden oder ein Talent erneut
// einladen. Löschen, Status setzen und CSV laufen immer, auch mit gesperrten
// Talenten (DSGVO).
export const SELECTION_GUARDED_ACTION_IDS = Object.freeze([
  SPACE_ACTION_IDS.CROSSMODE,
  SPACE_ACTION_IDS.SEND_REMINDER,
  SPACE_ACTION_IDS.RESET_EXPIRED,
  SPACE_ACTION_IDS.FULL_RESET
])

/**
 * owner der Auswahl für matchBatchIfYouCan im Kreuzvergleich. Gesperrte
 * Talente (Talentspeicher) gehen nie mit, auch wenn sie nach dem Guard in die
 * Auswahl kämen. Ohne Sperren fehlt storage_locked, dann wie bisher alle
 * owner der Auswahl.
 *
 * @param {Array} spaces ausgewählte Zeilen
 * @returns {string[]}
 */
export const crossmodeUsers = (spaces) =>
  (Array.isArray(spaces) ? spaces : [])
    .filter((space) => !!space && !isStorageLocked(space))
    .map(({ owner }) => owner)
    .filter((owner) => owner)

/**
 * Hängt den Auswahl-Guard an eine Massenaktion. Aktionen außerhalb von
 * SELECTION_GUARDED_ACTION_IDS bleiben dasselbe Objekt.
 *
 * Ohne gesperrte Talente läuft onClick wie bisher. Ist noch nicht gezählt
 * (pending), passiert nichts und die Auswahl bleibt stehen. Enthält die
 * Auswahl gesperrte Talente, ruft der Klick onBlocked mit dem Ergebnis von
 * checkSelection statt onClick.
 *
 * @param {object} action Aktion mit id und onClick
 * @param {{ spaces: Array, checkSelection: Function, onBlocked: Function }} guard
 */
export const withTalentStorageGuard = (
  action,
  { spaces, checkSelection, onBlocked }
) => {
  if (!action || !SELECTION_GUARDED_ACTION_IDS.includes(action.id)) {
    return action
  }

  return {
    ...action,
    onClick: (...args) => {
      const result = checkSelection(spaces)
      if (result?.pending) return undefined
      if (result && !result.allowed) {
        onBlocked(result)
        return undefined
      }

      return action.onClick?.(...args)
    }
  }
}

const toIdSet = (ids) => (ids instanceof Set ? ids : new Set(ids || []))

/**
 * Wählt die Zeilen mit den übergebenen IDs ab. Die Zeilen der Auswahl tragen
 * removeSelected aus selectionCallback in talents.js.
 *
 * @param {Array} spaces ausgewählte Zeilen
 * @param {Set<string>|Array<string>} ids
 * @returns {number} Anzahl der abgewählten Zeilen
 */
export const deselectTalents = (spaces, ids) => {
  const remove = toIdSet(ids)
  const rows = Array.isArray(spaces) ? spaces : []
  const matches = rows.filter(
    (space) =>
      !!space &&
      remove.has(space.id) &&
      typeof space.removeSelected === 'function'
  )

  matches.forEach((space) => space.removeSelected())

  return matches.length
}

/**
 * Optionen für den Limit-Dialog einer blockierten Massenaktion. „Gesperrte
 * abwählen" gibt es nur, wenn danach noch Talente in der Auswahl bleiben,
 * sonst wäre die Auswahl leer und die Select-Bar weg. Die Option wählt die
 * gesperrten Zeilen ab und ruft danach onContinue, damit die Aktion mit der
 * übrigen Auswahl weiterläuft. Ließ sich nichts abwählen, läuft nichts weiter.
 *
 * @param {{ spaces: Array, result: object, onContinue?: Function }} params
 *   result ist das Ergebnis von checkSelection
 * @returns {{ count: number, onDeselect?: Function }}
 */
export const selectionDialogOptions = ({ spaces, result, onContinue }) => {
  const rows = Array.isArray(spaces) ? spaces : []
  const lockedIds = toIdSet(result?.lockedIds)
  const count = result?.lockedCount ?? lockedIds.size
  const remaining = rows.filter(
    (space) => !!space && !lockedIds.has(space.id)
  ).length

  if (remaining === 0) return { count }

  return {
    count,
    onDeselect: () => {
      if (deselectTalents(rows, lockedIds) > 0) onContinue?.()
    }
  }
}

export const BULK_CONTINUATION = Object.freeze({
  WAIT: 'wait',
  RUN: 'run',
  DROP: 'drop'
})

/**
 * Entscheidet, ob eine Massenaktion nach „Gesperrte abwählen" weiterläuft.
 * continuation hält die Auswahl zum Zeitpunkt des Abwählens (fromSpaces) und
 * die abgewählten IDs (lockedIds). wait heißt, die Tabelle hat die neue
 * Auswahl noch nicht gemeldet. run heißt, die Auswahl ist neu und enthält
 * keine der abgewählten IDs mehr. drop heißt, die Auswahl hat sich anders
 * geändert oder ist leer, dann läuft nichts mehr.
 *
 * @param {{ fromSpaces: Array, lockedIds: Set<string>|Array<string> }|null} continuation
 * @param {Array} spaces aktuelle Auswahl
 * @returns {'wait'|'run'|'drop'}
 */
export const resolveBulkContinuation = (continuation, spaces) => {
  if (!continuation) return BULK_CONTINUATION.DROP
  if (spaces === continuation.fromSpaces) return BULK_CONTINUATION.WAIT

  const rows = Array.isArray(spaces) ? spaces.filter(Boolean) : []
  if (rows.length === 0) return BULK_CONTINUATION.DROP

  const lockedIds = toIdSet(continuation.lockedIds)

  return rows.some((space) => lockedIds.has(space.id))
    ? BULK_CONTINUATION.DROP
    : BULK_CONTINUATION.RUN
}
